// ADMIN — El Jarti Inglés: catálogo, pedidos y ajustes de la tienda.
//
// Esta página SÍ usa el design system oscuro de Jartinet (la estética Amazon es
// solo la cara pública de la tienda).
//
// Los productos se escriben directamente contra Firestore (reglas isAdmin, igual
// que packs_products); todo lo que toca dinero o pedidos pasa por Cloud Function.

import { useEffect, useMemo, useState } from 'react';
import { collection, query } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import type { JartiCatalogItem, JartiItemRef, JartiOrder, JartiProduct, JartiShopConfig } from '@/lib/types';
import {
  createJartiProduct, deleteJartiProduct, getJartiShopConfig, getModItemCatalog,
  jartiShop_adminOrderAction, jartiShop_cancelOrder, setJartiShopConfig, updateJartiProduct, uploadJartiImage,
} from '@/lib/actions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  ShoppingBag, Package, Plus, Trash2, Pencil, Loader2, Search, Upload, Box, Star,
  Settings, Truck, CheckCircle2, XCircle, Clock, RefreshCw, PackageCheck,
} from 'lucide-react';

type Tab = 'productos' | 'pedidos' | 'ajustes';

export default function AdminJartiInglesPage() {
  const { userData } = useAuth();
  const [tab, setTab] = useState<Tab>('productos');

  if (userData?.role !== 'admin') {
    return <p className="p-8 text-center text-muted-foreground">Solo administradores.</p>;
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-orange-500/15 via-card/40 to-card/20 p-6">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-orange-400/30 bg-orange-500/15 text-orange-400">
            <ShoppingBag className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-headline text-3xl font-bold">El Jarti Inglés</h1>
            <p className="text-sm text-muted-foreground">Catálogo, pedidos y entrega dentro del juego.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {([['productos', 'Productos', Package], ['pedidos', 'Pedidos', Truck], ['ajustes', 'Ajustes', Settings]] as const).map(([k, label, Icon]) => (
          <Button key={k} variant={tab === k ? 'default' : 'outline'} size="sm" onClick={() => setTab(k)}>
            <Icon className="mr-2 h-4 w-4" /> {label}
          </Button>
        ))}
      </div>

      {tab === 'productos' && <PanelProductos />}
      {tab === 'pedidos' && <PanelPedidos />}
      {tab === 'ajustes' && <PanelAjustes />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PRODUCTOS
// ════════════════════════════════════════════════════════════════════════════
const PRODUCTO_VACIO: Partial<JartiProduct> = {
  name: '', description: '', price: 100, imageUrl: '', category: '',
  items: [], stock: -1, maxPerOrder: 0, active: true, featured: false, order: 0,
};

function PanelProductos() {
  const { toast } = useToast();
  const [editing, setEditing] = useState<Partial<JartiProduct> | null>(null);
  const [busca, setBusca] = useState('');

  const productsQuery = useMemo(() => query(collection(db, 'jarti_products')), []);
  const { data: raw, isLoading } = useCollection<JartiProduct>(productsQuery);

  const products = useMemo(
    () => (raw || []).map((p) => sanitizeFirestoreData(p) as JartiProduct)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name || '').localeCompare(b.name || '')),
    [raw],
  );

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => `${p.name} ${p.category || ''}`.toLowerCase().includes(q));
  }, [products, busca]);

  const borrar = async (p: JartiProduct) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Los pedidos ya hechos no se ven afectados.`)) return;
    const r = await deleteJartiProduct(p.id);
    toast({ title: r.success ? 'Producto eliminado' : 'Error', description: r.message });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar producto…" className="pl-9" />
        </div>
        <Button onClick={() => setEditing({ ...PRODUCTO_VACIO })}>
          <Plus className="mr-2 h-4 w-4" /> Nuevo producto
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : visibles.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No hay productos todavía.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibles.map((p) => (
            <Card key={p.id} className="overflow-hidden">
              <div className="flex gap-3 p-3">
                <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted/40">
                  {p.imageUrl ? <img src={p.imageUrl} alt="" className="max-h-20 max-w-full object-contain" />
                    : <Package className="h-8 w-8 text-muted-foreground/40" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 truncate font-bold">{p.name || '(sin nombre)'}</p>
                    {p.featured && <Star className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" />}
                  </div>
                  <p className="text-sm text-primary">{(Number(p.price) || 0).toLocaleString('es-ES')} oro</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {p.category && <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>}
                    <Badge variant={p.active === false ? 'destructive' : 'outline'} className="text-[10px]">
                      {p.active === false ? 'Oculto' : 'Activo'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {typeof p.stock === 'number' && p.stock >= 0 ? `Stock ${p.stock}` : 'Stock ∞'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">{(p.items || []).length} ítems</Badge>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 border-t border-white/5 p-2">
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => setEditing(p)}>
                  <Pencil className="mr-2 h-3.5 w-3.5" /> Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => borrar(p)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <EditorProducto producto={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function EditorProducto({ producto, onClose }: { producto: Partial<JartiProduct>; onClose: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState<Partial<JartiProduct>>({ ...producto });
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  const set = (k: keyof JartiProduct, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const subirFoto = async (file?: File | null) => {
    if (!file) return;
    setSubiendo(true);
    const r = await uploadJartiImage(file);
    setSubiendo(false);
    if (r.success && r.url) set('imageUrl', r.url);
    else toast({ title: 'Error al subir', description: r.message, variant: 'destructive' });
  };

  const guardar = async () => {
    if (!form.name?.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    setGuardando(true);
    const payload: Partial<JartiProduct> = {
      ...form,
      price: Math.max(0, Math.floor(Number(form.price) || 0)),
      stock: Number.isFinite(Number(form.stock)) ? Math.floor(Number(form.stock)) : -1,
      maxPerOrder: Math.max(0, Math.floor(Number(form.maxPerOrder) || 0)),
      order: Math.floor(Number(form.order) || 0),
    };
    const r = form.id ? await updateJartiProduct(form.id, payload) : await createJartiProduct(payload);
    setGuardando(false);
    toast({ title: r.success ? 'Guardado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
    if (r.success) onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90dvh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{form.id ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Juego de candelabros de cola" />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Input value={form.category || ''} onChange={(e) => set('category', e.target.value)} placeholder="Priostía" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea rows={3} value={form.description || ''} onChange={(e) => set('description', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Precio (oro)</Label>
              <Input type="number" min={0} value={form.price ?? 0} onChange={(e) => set('price', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Stock (−1 = ∞)</Label>
              <Input type="number" value={form.stock ?? -1} onChange={(e) => set('stock', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Máx./pedido (0 = ∞)</Label>
              <Input type="number" min={0} value={form.maxPerOrder ?? 0} onChange={(e) => set('maxPerOrder', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Orden</Label>
              <Input type="number" value={form.order ?? 0} onChange={(e) => set('order', e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Foto del producto</Label>
            <div className="flex items-center gap-3">
              <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-lg border border-white/10 bg-muted/30">
                {form.imageUrl ? <img src={form.imageUrl} alt="" className="max-h-24 max-w-full object-contain" />
                  : <Package className="h-8 w-8 text-muted-foreground/40" />}
              </div>
              <div className="flex-1 space-y-2">
                <Input value={form.imageUrl || ''} onChange={(e) => set('imageUrl', e.target.value)} placeholder="URL de la imagen" />
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 text-sm hover:bg-white/5">
                  {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Subir archivo
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => subirFoto(e.target.files?.[0])} />
                </label>
              </div>
            </div>
          </div>

          <Separator />

          <SelectorItems items={form.items || []} onChange={(items) => set('items', items)} />

          <Separator />

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2">
              <Switch checked={form.active !== false} onCheckedChange={(v) => set('active', v)} />
              <span className="text-sm">Visible en la tienda</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch checked={form.featured === true} onCheckedChange={(v) => set('featured', v)} />
              <span className="text-sm">Destacado</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button onClick={guardar} disabled={guardando}>
              {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Selector de los ítems que se entregan. La lista de ids REALES la reporta el
 * servidor en su latido (mod_item_catalog), así que siempre está en sync con el
 * mod; si el servidor no ha latido aún, se puede escribir el id a mano.
 */
function SelectorItems({ items, onChange }: { items: JartiItemRef[]; onChange: (v: JartiItemRef[]) => void }) {
  const [catalogo, setCatalogo] = useState<JartiCatalogItem[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busca, setBusca] = useState('');
  const [manual, setManual] = useState('');

  const cargar = async () => {
    setCargando(true);
    const r = await getModItemCatalog();
    setCatalogo(r.items || []);
    setCargando(false);
  };
  useEffect(() => { cargar(); }, []);

  const resultados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return [];
    return catalogo.filter((i) => i.id.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)).slice(0, 40);
  }, [catalogo, busca]);

  const add = (id: string) => {
    if (!id.trim()) return;
    if (items.some((i) => i.id === id)) return;
    onChange([...items, { id: id.trim(), count: 1 }]);
    setBusca('');
    setManual('');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2"><Box className="h-4 w-4" /> Qué se entrega en el juego</Label>
        <Button size="sm" variant="ghost" onClick={cargar} disabled={cargando}>
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${cargando ? 'animate-spin' : ''}`} />
          {catalogo.length > 0 ? `${catalogo.length} ítems` : 'Recargar catálogo'}
        </Button>
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-2 rounded-lg border border-white/8 bg-card/40 px-3 py-2">
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{it.id}</span>
              <Input
                type="number" min={1} className="w-20"
                value={it.count}
                onChange={(e) => {
                  const copy = [...items];
                  copy[i] = { ...it, count: Math.max(1, Math.floor(Number(e.target.value) || 1)) };
                  onChange(copy);
                }}
              />
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onChange(items.filter((x) => x.id !== it.id))}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar ítem del mod (cirio, faldón, candelabro…)" className="pl-9" />
      </div>

      {resultados.length > 0 && (
        <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-white/8 p-1">
          {resultados.map((r) => (
            <button
              key={r.id}
              onClick={() => add(r.id)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-white/5"
            >
              <Plus className="h-3.5 w-3.5 text-primary" />
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{r.id}</span>
            </button>
          ))}
        </div>
      )}

      {catalogo.length === 0 && !cargando && (
        <p className="text-xs text-muted-foreground">
          El servidor todavía no ha enviado su catálogo de ítems (llega en el latido, en menos de un minuto
          con el servidor encendido). Mientras tanto puedes escribir el id a mano.
        </p>
      )}

      <div className="flex gap-2">
        <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="example:cirio_rojo" className="font-mono text-xs" />
        <Button variant="outline" onClick={() => add(manual)}>Añadir id</Button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PEDIDOS
// ════════════════════════════════════════════════════════════════════════════
function PanelPedidos() {
  const { toast } = useToast();
  const [filtro, setFiltro] = useState<'todos' | 'preparando' | 'listo' | 'entregado' | 'cancelado'>('todos');
  const [busca, setBusca] = useState('');
  const [trabajando, setTrabajando] = useState('');

  const ordersQuery = useMemo(() => query(collection(db, 'jarti_orders')), []);
  const { data: raw, isLoading } = useCollection<JartiOrder>(ordersQuery);

  const orders = useMemo(
    () => (raw || []).map((o) => sanitizeFirestoreData(o) as JartiOrder)
      .sort((a, b) => msOf(b.createdAt) - msOf(a.createdAt)),
    [raw],
  );

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return orders.filter((o) => {
      if (filtro !== 'todos' && o.status !== filtro) return false;
      if (!q) return true;
      return `${o.code} ${o.mcName} ${o.userName || ''}`.toLowerCase().includes(q);
    });
  }, [orders, filtro, busca]);

  const contar = (s: string) => orders.filter((o) => o.status === s).length;

  const accion = async (o: JartiOrder, action: 'ready' | 'deliver' | 'cancel') => {
    if (action === 'cancel' && !window.confirm(`¿Cancelar ${o.code} y devolver ${o.total} oro?`)) return;
    setTrabajando(o.id);
    const r = action === 'cancel'
      ? await jartiShop_cancelOrder(o.id)
      : await jartiShop_adminOrderAction(o.id, action);
    setTrabajando('');
    toast({ title: r?.success ? 'Hecho' : 'Error', description: r?.message, variant: r?.success ? undefined : 'destructive' });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {([['preparando', 'Preparando', Clock, 'text-amber-400'],
          ['listo', 'Listos', PackageCheck, 'text-emerald-400'],
          ['entregado', 'Entregados', CheckCircle2, 'text-sky-400'],
          ['cancelado', 'Cancelados', XCircle, 'text-rose-400']] as const).map(([k, label, Icon, color]) => (
          <button key={k} onClick={() => setFiltro(filtro === k ? 'todos' : k)}
            className={`rounded-2xl border p-3 text-left transition-colors ${filtro === k ? 'border-primary/40 bg-primary/10' : 'border-white/8 bg-card/40 hover:bg-white/5'}`}>
            <Icon className={`h-5 w-5 ${color}`} />
            <p className="mt-1 text-2xl font-bold">{contar(k)}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por código, jugador o usuario…" className="pl-9" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : visibles.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No hay pedidos que mostrar.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {visibles.map((o) => (
            <Card key={o.id} className="overflow-hidden">
              <CardContent className="flex flex-wrap items-center gap-4 p-4">
                {o.mcUuid && (
                  <img src={`https://mc-heads.net/avatar/${o.mcUuid}/40`} alt="" className="h-10 w-10 rounded" style={{ imageRendering: 'pixelated' }} />
                )}
                <div className="min-w-[180px] flex-1">
                  <p className="font-bold">{o.code}</p>
                  <p className="text-xs text-muted-foreground">
                    {o.mcName || '—'} · {o.userName || o.uid.slice(0, 8)} · {fechaHora(o.createdAt)}
                  </p>
                </div>
                <div className="min-w-[160px] flex-1 text-sm text-muted-foreground">
                  {(o.lines || []).map((l) => `${l.qty}× ${l.name}`).join(', ')}
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">{(Number(o.total) || 0).toLocaleString('es-ES')} oro</p>
                  <EstadoBadge status={o.status} />
                </div>
                <div className="flex gap-2">
                  {o.status === 'preparando' && (
                    <Button size="sm" variant="outline" disabled={trabajando === o.id} onClick={() => accion(o, 'ready')}>
                      Marcar listo
                    </Button>
                  )}
                  {o.status === 'listo' && (
                    <Button size="sm" variant="outline" disabled={trabajando === o.id} onClick={() => accion(o, 'deliver')}>
                      Entregado a mano
                    </Button>
                  )}
                  {o.status !== 'entregado' && o.status !== 'cancelado' && (
                    <Button size="sm" variant="ghost" className="text-destructive" disabled={trabajando === o.id} onClick={() => accion(o, 'cancel')}>
                      Cancelar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function EstadoBadge({ status }: { status: JartiOrder['status'] }) {
  const map: Record<string, { label: string; className: string }> = {
    preparando: { label: 'Preparando', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    listo: { label: 'Listo', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    entregado: { label: 'Entregado', className: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
    cancelado: { label: 'Cancelado', className: 'bg-rose-500/15 text-rose-400 border-rose-500/30' },
  };
  const s = map[status] || map.preparando;
  return <Badge variant="outline" className={`text-[10px] ${s.className}`}>{s.label}</Badge>;
}

// ════════════════════════════════════════════════════════════════════════════
// AJUSTES
// ════════════════════════════════════════════════════════════════════════════
function PanelAjustes() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState<JartiShopConfig>({ preparationMinutes: 120, open: true, announcement: '' });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      const c = await getJartiShopConfig();
      setCfg({
        preparationMinutes: c.preparationMinutes ?? 120,
        open: c.open !== false,
        announcement: c.announcement || '',
      });
      setCargando(false);
    })();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    const r = await setJartiShopConfig({
      preparationMinutes: Math.max(0, Math.floor(Number(cfg.preparationMinutes) || 0)),
      open: cfg.open !== false,
      announcement: cfg.announcement || '',
    });
    setGuardando(false);
    toast({ title: r.success ? 'Ajustes guardados' : 'Error', description: r.message });
  };

  if (cargando) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Ajustes de la tienda</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label>Tiempo de preparación (minutos)</Label>
          <Input
            type="number" min={0} className="max-w-[200px]"
            value={cfg.preparationMinutes ?? 120}
            onChange={(e) => setCfg((c) => ({ ...c, preparationMinutes: Number(e.target.value) }))}
          />
          <p className="text-xs text-muted-foreground">
            Lo que tarda un pedido en pasar a "listo para recoger". Por defecto 120 (2 horas).
            Al cumplirse, el pedido cambia solo y le llega una carta al buzón del comprador.
          </p>
        </div>

        <Separator />

        <label className="flex items-center gap-3">
          <Switch checked={cfg.open !== false} onCheckedChange={(v) => setCfg((c) => ({ ...c, open: v }))} />
          <span>
            <span className="block text-sm font-medium">Tienda abierta</span>
            <span className="block text-xs text-muted-foreground">Si la cierras, nadie puede hacer pedidos nuevos (los existentes siguen su curso).</span>
          </span>
        </label>

        <Separator />

        <div className="space-y-2">
          <Label>Aviso en la cabecera</Label>
          <Textarea
            rows={2}
            value={cfg.announcement || ''}
            onChange={(e) => setCfg((c) => ({ ...c, announcement: e.target.value }))}
            placeholder="Ej.: Envíos suspendidos durante la Madrugá."
          />
        </div>

        <Button onClick={guardar} disabled={guardando}>
          {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar ajustes
        </Button>
      </CardContent>
    </Card>
  );
}

// ── helpers ──
function msOf(v: any): number {
  if (!v) return 0;
  if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? 0 : t; }
  if (v?.toDate) return v.toDate().getTime();
  if (typeof v === 'number') return v;
  return 0;
}

function fechaHora(v: any) {
  const ms = msOf(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
